import { formatInvoiceSpecs } from '@/lib/invoiceSpecs';
import React from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { fetchPublicInvoiceByToken, PublicInvoiceData } from '@/services/invoiceShare';
import {
  Receipt,
  Printer,
  PhoneCall,
  CheckCircle2,
  AlertTriangle,
  ShieldCheck,
  Loader2,
} from 'lucide-react';

function formatCurrency(val: number | null | undefined): string {
  if (val == null) return 'Rs. 0';
  return `Rs. ${Number(val).toLocaleString('en-PK')}`;
}

export const PublicInvoicePage: React.FC = () => {
  const { token = '' } = useParams<{ token: string }>();

  const { data: invoice, isLoading, isError } = useQuery<PublicInvoiceData>({
    queryKey: ['public_invoice', token],
    queryFn: () => fetchPublicInvoiceByToken(token),
    enabled: !!token,
  });

  const handlePrint = () => {
    window.print();
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-brand-bg flex flex-col items-center justify-center p-6 text-center space-y-3">
        <Loader2 className="w-10 h-10 text-brand-green animate-spin" />
        <p className="font-bold text-sm text-brand-black">Loading official invoice…</p>
        <p className="text-xs text-brand-muted">Verifying secure digital invoice share link…</p>
      </div>
    );
  }

  if (isError || invoice?.error) {
    const errCode = invoice?.error || 'error';
    return (
      <div className="min-h-screen bg-brand-bg flex flex-col items-center justify-center p-6 font-sans">
        <div className="bg-white rounded-2xl border border-brand-border p-8 max-w-md w-full text-center space-y-4 shadow-sm">
          <div className="w-14 h-14 bg-red-50 text-brand-red rounded-full flex items-center justify-center mx-auto">
            <AlertTriangle className="w-8 h-8" />
          </div>
          <h1 className="text-lg font-extrabold text-brand-black">
            {errCode === 'token_expired'
              ? 'Invoice Link Expired'
              : errCode === 'token_revoked'
              ? 'Invoice Link Revoked'
              : 'Invoice Not Available'}
          </h1>
          <p className="text-xs text-brand-muted">
            {errCode === 'token_expired'
              ? 'This secure invoice link has reached its expiration period. Please contact Al Kuwait Tech to request a fresh invoice link.'
              : errCode === 'token_revoked'
              ? 'This share link was updated or revoked by the store administrator.'
              : 'The requested invoice could not be found or verified.'}
          </p>
          <div className="pt-2">
            <a
              href="https://wa.me/923000000000"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center space-x-1.5 px-4 py-2 bg-brand-green text-white text-xs font-bold rounded-lg hover:bg-brand-green-hover"
            >
              <PhoneCall className="w-3.5 h-3.5" />
              <span>Contact Store Support</span>
            </a>
          </div>
        </div>
      </div>
    );
  }

  if (!invoice) return null;

  return (
    <div className="min-h-screen bg-brand-bg py-6 px-4 sm:px-8 font-sans text-xs print:bg-white print:p-0">
      {/* Top Action Bar (Hidden when printing) */}
      <div className="max-w-3xl mx-auto mb-4 flex items-center justify-between print:hidden">
        <div className="flex items-center space-x-2">
          <Receipt className="w-5 h-5 text-brand-green" />
          <span className="font-bold text-sm text-brand-black">Official Customer Invoice</span>
        </div>
        <div className="flex items-center space-x-2">
          <button
            type="button"
            onClick={handlePrint}
            className="inline-flex items-center space-x-1.5 px-3.5 py-1.5 bg-brand-black text-white text-xs font-bold rounded-md hover:bg-neutral-800 transition-colors shadow-xs"
          >
            <Printer className="w-3.5 h-3.5" />
            <span>Print / Save PDF</span>
          </button>
        </div>
      </div>

      {/* Invoice Sheet */}
      <div className="max-w-3xl mx-auto bg-white rounded-xl border border-brand-border p-6 sm:p-10 shadow-sm print:border-none print:shadow-none print:p-0 space-y-6">
        {/* Invoice Header */}
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 pb-6 border-b border-brand-border">
          <div className="flex items-start space-x-3">
            <div className="w-12 h-12 bg-brand-green/10 text-brand-green font-extrabold text-xl rounded-lg flex items-center justify-center shrink-0">
              AK
            </div>
            <div>
              <h1 className="text-base font-extrabold text-brand-black uppercase tracking-wide">
                {invoice.shop?.shop_name || 'AL KUWAIT TECH'}
              </h1>
              <p className="text-[11px] text-brand-muted">{invoice.shop?.slogan || 'Laptops, Computers & Certified Repair Services'}</p>
              <p className="text-[10px] text-brand-muted mt-0.5">
                {invoice.shop?.address
                  ? `${invoice.shop.address}${invoice.shop.city ? ', ' + invoice.shop.city : ''}`
                  : 'Shop 264, 2nd Floor Umair Center, 6th Road, Rawalpindi'}
              </p>
              <p className="text-[10px] text-brand-muted">
                Support: {invoice.shop?.phone || invoice.shop?.phone_secondary || '+92 370 5478785'}
                {invoice.shop?.whatsapp && ` | WhatsApp: ${invoice.shop.whatsapp}`}
              </p>
            </div>
          </div>

          <div className="text-left sm:text-right space-y-1">
            <span className="inline-block px-2.5 py-1 bg-brand-bg border border-brand-border font-mono font-bold text-xs rounded text-brand-black">
              {invoice.invoice_number}
            </span>
            <p className="text-[11px] text-brand-muted">
              Date: <strong className="text-brand-black">{new Date(invoice.sale_date).toLocaleDateString('en-PK', { year: 'numeric', month: 'short', day: 'numeric' })}</strong>
            </p>
            <span
              className={`inline-block px-2 py-0.5 rounded text-[10px] font-bold uppercase ${
                invoice.payment_status === 'paid'
                  ? 'bg-green-100 text-green-800'
                  : invoice.payment_status === 'partial'
                  ? 'bg-amber-100 text-amber-800'
                  : 'bg-red-100 text-red-800'
              }`}
            >
              Payment: {invoice.payment_status}
            </span>
          </div>
        </div>

        {/* Bill To Info */}
        <div className="p-3.5 bg-brand-bg/60 rounded-lg border border-brand-border/80 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-brand-muted">Billed To</p>
            <p className="font-bold text-brand-black text-sm">{invoice.customer_name}</p>
          </div>
          <div className="text-left sm:text-right text-[11px] text-brand-muted font-mono">
            {invoice.customer_phone && <p>Tel: {invoice.customer_phone}</p>}
            {invoice.customer_email && <p>Email: {invoice.customer_email}</p>}
          </div>
        </div>

        {/* Line Items Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-brand-bg border-b border-brand-border text-brand-muted font-bold uppercase text-[10px]">
              <tr>
                <th className="text-left px-3 py-2.5">#</th>
                <th className="text-left px-3 py-2.5">Item & Specifications</th>
                <th className="text-center px-3 py-2.5">Qty</th>
                <th className="text-right px-3 py-2.5">Unit Price</th>
                <th className="text-right px-3 py-2.5">Discount</th>
                <th className="text-right px-3 py-2.5">Amount</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-brand-border">
              {invoice.items.map((item, idx) => (
                <tr key={idx}>
                  <td className="px-3 py-2.5 text-brand-muted">{idx + 1}</td>
                  <td className="px-3 py-2.5">
                    <p className="font-bold text-brand-black">{item.item_name}</p>
                    {formatInvoiceSpecs({ ...item, specs_snapshot: item.specs }) && (
                      <p className="text-[11px] text-brand-green font-semibold mt-0.5">{formatInvoiceSpecs({ ...item, specs_snapshot: item.specs })}</p>
                    )}
                    {(item.serial_number || item.sku) && (
                      <p className="text-[10px] text-brand-muted font-mono">
                        {item.serial_number ? `S/N: ${item.serial_number}` : `SKU: ${item.sku}`}
                      </p>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-center font-medium">{item.quantity}</td>
                  <td className="px-3 py-2.5 text-right font-medium">{formatCurrency(item.unit_price)}</td>
                  <td className="px-3 py-2.5 text-right text-brand-red font-medium">
                    {item.line_discount > 0 ? `- ${formatCurrency(item.line_discount)}` : '—'}
                  </td>
                  <td className="px-3 py-2.5 text-right font-bold text-brand-black">
                    {formatCurrency(item.line_total)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Financial Breakdown Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4 border-t border-brand-border">
          {/* Warranty & Terms */}
          <div className="space-y-2">
            <div className="p-3 bg-brand-bg rounded-lg border border-brand-border space-y-1">
              <p className="font-bold text-brand-black flex items-center space-x-1">
                <ShieldCheck className="w-3.5 h-3.5 text-brand-green" />
                <span>Warranty & Support Terms</span>
              </p>
              <p className="text-[11px] text-brand-muted leading-relaxed">
                {invoice.shop?.default_warranty_months
                  ? `Certified hardware warranty (${invoice.shop.default_warranty_months} month${invoice.shop.default_warranty_months > 1 ? 's' : ''}) covering technical defects. Physical damage, liquid spills, and software issues are excluded. Please present this invoice for warranty verification.`
                  : 'Certified hardware warranty covering technical defects. Physical damage, liquid spills, and software issues are excluded. Please present this invoice for warranty verification.'}
              </p>
            </div>
          </div>

          {/* Totals Box */}
          <div className="p-3.5 bg-brand-bg rounded-lg border border-brand-border space-y-1.5 text-xs">
            <div className="flex justify-between text-brand-muted">
              <span>Subtotal:</span>
              <span className="font-semibold text-brand-black">{formatCurrency(invoice.subtotal)}</span>
            </div>
            {invoice.discount > 0 && (
              <div className="flex justify-between text-amber-700">
                <span>Invoice Discount:</span>
                <span className="font-semibold">- {formatCurrency(invoice.discount)}</span>
              </div>
            )}
            {invoice.tax > 0 && (
              <div className="flex justify-between text-brand-muted">
                <span>Sales Tax:</span>
                <span className="font-semibold text-brand-black">{formatCurrency(invoice.tax)}</span>
              </div>
            )}
            <div className="flex justify-between pt-1.5 border-t border-brand-border font-bold text-sm">
              <span className="text-brand-black">Grand Total:</span>
              <span className="text-brand-green">{formatCurrency(invoice.total)}</span>
            </div>
            <div className="flex justify-between pt-1 border-t border-brand-border/60 text-brand-muted">
              <span>Amount Paid:</span>
              <span className="font-bold text-brand-black">{formatCurrency(invoice.amount_paid)}</span>
            </div>
            <div className="flex justify-between font-bold">
              <span>Balance Due:</span>
              <span className={invoice.balance_due > 0 ? 'text-brand-red' : 'text-brand-green'}>
                {formatCurrency(invoice.balance_due)}
              </span>
            </div>
          </div>
        </div>

        {/* Payments History */}
        {invoice.payments && invoice.payments.length > 0 && (
          <div className="pt-3 border-t border-brand-border">
            <div className="flex items-center justify-between mb-2">
              <p className="text-[10px] font-bold uppercase tracking-wider text-brand-muted">
                Payment Transactions
              </p>
              <div className="text-[10px] text-brand-muted space-x-2 font-mono">
                <span>Received: <strong className="text-brand-green">{formatCurrency(invoice.payments.filter((p: any) => p.direction !== 'out').reduce((s: number, p: any) => s + (p.amount || 0), 0))}</strong></span>
                {invoice.payments.some((p: any) => p.direction === 'out') && (
                  <span>Refunded: <strong className="text-brand-red">{formatCurrency(invoice.payments.filter((p: any) => p.direction === 'out').reduce((s: number, p: any) => s + (p.amount || 0), 0))}</strong></span>
                )}
              </div>
            </div>
            <div className="space-y-1">
              {invoice.payments.map((pmt: any, i: number) => {
                const isRefund = pmt.direction === 'out';
                return (
                  <div
                    key={i}
                    className={`flex items-center justify-between p-2 rounded border text-[11px] ${isRefund ? 'bg-amber-50/50 border-amber-200' : 'bg-slate-50 border-slate-200'}`}
                  >
                    <div className="flex items-center space-x-2">
                      <CheckCircle2 className={`w-3.5 h-3.5 ${isRefund ? 'text-amber-600' : 'text-brand-green'}`} />
                      <span className="font-semibold capitalize text-brand-black">
                        {isRefund ? 'Refund' : 'Payment'} ({pmt.payment_method?.replace('_', ' ') || 'Cash'})
                      </span>
                      {pmt.reference && (
                        <span className="text-brand-muted font-mono">Ref: {pmt.reference}</span>
                      )}
                    </div>
                    <div className="flex items-center space-x-2 font-mono">
                      <span className="text-brand-muted">
                        {new Date(pmt.payment_date).toLocaleDateString('en-PK')}
                      </span>
                      <strong className={isRefund ? 'text-brand-red font-bold' : 'text-brand-black'}>
                        {isRefund ? '-' : ''}{formatCurrency(pmt.amount)}
                      </strong>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Invoice Footer */}
        <div className="pt-6 border-t border-brand-border text-center text-[10px] text-brand-muted space-y-1">
          <p className="font-bold text-brand-black">
            {invoice.shop?.invoice_footer || `Thank you for choosing ${invoice.shop?.shop_name || 'AL KUWAIT TECH'}. We appreciate your business!`}
          </p>
          <p>For support or service tracking, contact our hotline at {invoice.shop?.phone || invoice.shop?.phone_secondary || '+92 370 5478785'}</p>
        </div>
      </div>
    </div>
  );
};
